import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { IHelpdeskLocationResponse, fetchHeldeskLocations } from '../../../../libraries/location';
import { AppState } from '../../../client-redux/reducers';
import { useFitTableRows } from '../../../components/util/fit-table-rows-hook';
import { Group, Loader, Table } from '@mantine/core';
import PaginationComponent from '../../../components/pagination';
import Header from '../../../components/util/header';
import HelpdeskFilterBar, { HelpdeskFilterBarFieldsData } from '../../components/helpdesk-filterbar';
import * as Icon from '@tabler/icons-react';
import './styles.scss';

const HelpdeskLocationList = () => {
  const lang = useSelector((state: AppState) => state.lang.lang);
  const location = useLocation();
  const controller = new AbortController();
  const searchParams = new URLSearchParams(location.search);
  const navigate = useNavigate();

  const defaultEntid = Number(searchParams.get('entid')) || 0;

  const [list, setList] = useState<IHelpdeskLocationResponse>();
  const [, /* entid */ setEntid] = useState<number>(defaultEntid);

  const [totalRowsResponse, setTotalRowsResponse] = useState<number | undefined>();
  const [offset, setOffset] = useState<number>(0);
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: 0,
    minHeightForDynamic: 400
  });
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [noResults, setNoResults] = useState<boolean>(true);
  const [statusOperCode, setStatusOperCode] = useState<number>();
  const [newLocation, setNewLocation] = useState<boolean>(false);
  const [filterBarData, setFilterBarData] = useState<HelpdeskFilterBarFieldsData>();

  useEffect(() => {
    if (filterBarData && limit) {
      loadLocationsList();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData, limit, offset]);

  const handleFilterBarOnDataChange = (data: HelpdeskFilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  const loadLocationsList = () => {
    if (loading) {
      setNoResults(false);
      controller.abort();
    }
    setLoading(true);
    setList(undefined);
    const shouldFetchTotalRows = offset === 0;
    fetchHeldeskLocations(limit, offset, shouldFetchTotalRows, filterBarData?.entity, filterBarData?.name)
      .then(response => {
        if (response.statusOper.code === 0) {
          setList(response);
          if (shouldFetchTotalRows && typeof response.totalRows === 'number') {
            setTotalRowsResponse(response.totalRows);
          }
          setNoResults(false);
          setStatusOperCode(response.statusOper.code);
        } else {
          setNoResults(true);
          setStatusOperCode(response.statusOper.code);
        }
      })
      .catch(error => {
        if (error.name === 'AbortError') {
        } else {
        }
      })
      .finally(() => setLoading(false));
  };

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  const toggleNewLocation = () => {
    setNewLocation(!newLocation);
    navigate(`/helpdesk/location/new/0/0`);
  };

  return (
    <div className="helpdesk-locals">
      <Header pageTitle={lang.GLOBAL_LOCATIONS} />
      <HelpdeskFilterBar
        onSearch={loadLocationsList}
        onDataChange={handleFilterBarOnDataChange}
        fieldsUsage={{
          entity: true,
          name: true
        }}
        addButton={toggleNewLocation}
      />
      <Table.ScrollContainer minWidth={500} ref={tableRef}>
        <Table striped highlightOnHover verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{lang.ENTITY_ID}</Table.Th>
              <Table.Th>ID</Table.Th>
              <Table.Th>{lang.GLOBAL_NAME}</Table.Th>
              <Table.Th>{lang.GLOBAL_ADDRESS}</Table.Th>
              <Table.Th>{lang.EVENT_ZIP_CODE}</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            ) : noResults ? (
              <Table.Tr>
                <Table.Td colSpan={6} style={{ textAlign: 'center' }}>
                  {lang.GLOBAL_NO_RESULTS}
                </Table.Td>
              </Table.Tr>
            ) : (
              list?.list.map((list, index) => (
                <Table.Tr
                  key={index}
                  onClick={() => {
                    setEntid(list.entid);
                    navigate(`/helpdesk/location/read/${list.entid}/${list.sid}`);
                  }}
                >
                  <Table.Td>{list.entid}</Table.Td>
                  <Table.Td>{list.sid}</Table.Td>
                  <Table.Td>{list.name}</Table.Td>
                  <Table.Td>{list.address}</Table.Td>
                  <Table.Td>{list.postalCode}</Table.Td>
                  <Table.Td>
                    <Icon.IconEdit
                      stroke={2}
                      style={{ cursor: 'pointer' }}
                      onClick={event => {
                        event.stopPropagation();
                        setEntid(list.entid);
                        navigate(`/helpdesk/location/edit/${list.entid}/${list.sid}`);
                      }}
                    />
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Group>
        <PaginationComponent
          total={totalRowsResponse}
          statusOperCode={statusOperCode}
          itemsPerPage={limit}
          dynamicLimit={limit}
          currentPage={currentPage}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          offset={offset}
        />
      </Group>
    </div>
  );
};

export default HelpdeskLocationList;
